import {
  prepareSelectOptions,
  scrollToError,
  getBrowserLocale,
  debounce,
  prepareResponse,
  getValueFromObjectItem,
  getValueFromArr,
  sumOfArrayNumbers,
  initEmailMask,
  initPhoneMask,
  initInnMask,
  initKppMask,
  initSnilsMask,
  isValidDate,
  formatDate,
  formatMoney,
  getDeclinationFromNumber,
  findOne,
  findAll,
  firstToUppercase,
  maskDate,
  isObject,
  getUniqueKey,
  required,
  cutString,
  arrayToCutString,
  getDeepCopyObject,
  getNowDate,
  isEqual,
  isArraysEqual,
  areArraysEqual,
  unflatenObject,
  flattenObject,
  mountSubFilter,
  tableColumnsUtils,
} from 'r3ui-lib';
import { navigateTo, showError, isRef } from '#imports'; // Added isRef
import Cookies from 'js-cookie';
import type { SidebarLink } from '~/types/components/sidebar.types';
import { render } from 'vue';
import { translate } from '~/i18n/i18n.config';
import constants from './constants';
import type { Ref } from '#imports';

export const { prepareColumnsToSave } = tableColumnsUtils;

export function fatalErrorHandler(response: Response) {
  if (response.status === 500) {
    showError({
      statusCode: response.status,
      statusMessage: '',
      fatal: true,
    });
  }
  if (response.status === 401) {
    Cookies.remove('auth_token');
    navigateTo('/login', { external: true });
  }
}

export function getChildrensIsActive(links: SidebarLink[]) {
  if (!links?.length) return false;
  if (!links[0].childrens?.length) return false;
  return (
    links[0].childrens.find(item => Boolean(item.isActive))?.isActive || false
  );
}

export function redirectTo(url: string) {
  if (url.length < 1) {
    return false;
  }
  navigateTo(url);
}

type OwnerNameData = {
  ownership_form_id?: number;
  organization_name?: string;
  first_name?: string;
  second_name?: string;
};

/**
 * Определяет имя владельца на основе формы владения (Strategy Pattern)
 * @param owner - Данные владельца
 * @param fallback - Значение по умолчанию, если имя не найдено
 * @returns Имя владельца
 */
export function getOwnerName(
  owner: OwnerNameData | null | undefined,
  fallback: string = '-'
): string {
  if (!owner) {
    return fallback;
  }

  const { ownership_form_id, organization_name, first_name, second_name } =
    owner;

  const isOrganization =
    ownership_form_id === 1 ||
    ownership_form_id === constants.ownershipForm.ORGANIZATION;

  if (isOrganization) {
    return organization_name || fallback;
  }

  const fullName = [first_name, second_name].filter(Boolean).join(' ').trim();

  return fullName || fallback;
}

/**
 * Определяет название пола животного (Strategy Pattern)
 * @param genderId - ID пола
 * @param fallback - Значение по умолчанию
 * @returns Название пола
 */
export function getGenderName(
  genderId: number | string | null | undefined,
  fallback: string = '-'
): string {
  if (!genderId) {
    return fallback;
  }

  const genderMap: Record<number, string> = {
    [constants.gender.MALE]: translate('male'),
    [constants.gender.FEMALE]: translate('female'),
  };

  return genderMap[Number(genderId)] || fallback;
}

type FilterValue =
  | string
  | number
  | (string | number)[]
  | Record<string, unknown>
  | null
  | undefined;
type FilterSet = Record<string, FilterValue>;

export function getFilterData(tableFilters: FilterSet | Ref<FilterSet>) {
  const filters = isRef(tableFilters) ? tableFilters.value : tableFilters;

  const result: Record<string, FilterValue> = {};

  for (const [key, value] of Object.entries({
    ...filters,
  })) {
    let keep = true;
    if (value === null || value === undefined) {
      keep = false;
    } else if (typeof value === 'string' && value.length === 0) {
      keep = false;
    } else if (Array.isArray(value)) {
      // Проверяем на пустой массив ИЛИ массив с одним пустым элементом
      if (
        value.length === 0 ||
        value[0] === null ||
        value[0] === undefined ||
        value[0] === ''
      ) {
        keep = false;
      }
    } else if (typeof value === 'object' && value !== null) {
      if (Object.keys(value).length === 0) {
        keep = false;
      }
    }

    if (keep) {
      result[key] = value as FilterValue;
    }
  }
  return result;
}

interface ExtendedOptionInterface {
  id: number;
  label: string;

  [key: string]: unknown;
}

interface SimpleOptionInterface {
  id: number;
  label: string;
}

interface LabelByIdInterface {
  id: number;
  arr: ExtendedOptionInterface[] | SimpleOptionInterface[];
  field: string;
}

export function getLabelById({ id, arr, field }: LabelByIdInterface): string {
  if (!id || arr.length === 0) return '-';
  const res = arr.find(i => i.id === id) as ExtendedOptionInterface;

  if (res && Object.prototype.hasOwnProperty.call(res, field)) {
    const value = res[field];
    if (value !== null && value !== undefined) {
      return String(value);
    }
  }
  return '-';
}

export function isDayType(type: number | null) {
  return String(type) === String(constants.timeTypes.DAY);
}

export function isWeekType(type: number | null) {
  return String(type) === String(constants.timeTypes.WEEK);
}

export function isMonthType(type: number | null) {
  return String(type) === String(constants.timeTypes.MONTH);
}

export function isYearType(type: number | null) {
  return String(type) === String(constants.timeTypes.YEAR);
}

export function getMonthsFromYears(years: number | null) {
  return Number(years) * 12;
}

export function getDaysFromYears(years: number | null) {
  return Number(years) * 365;
}

export function getDaysFromMonth(months: number | null) {
  return Number(months) * 30;
}

export function getDaysFromWeeks(weeks: number | null) {
  return Number(weeks) * 7;
}

export {
  prepareSelectOptions,
  scrollToError,
  getBrowserLocale,
  debounce,
  prepareResponse,
  getValueFromObjectItem,
  getValueFromArr,
  sumOfArrayNumbers,
  initEmailMask,
  initPhoneMask,
  initInnMask,
  initKppMask,
  initSnilsMask,
  isValidDate,
  formatDate,
  formatMoney,
  getDeclinationFromNumber,
  findOne,
  findAll,
  firstToUppercase,
  maskDate,
  isObject,
  getUniqueKey,
  required,
  cutString,
  arrayToCutString,
  getDeepCopyObject,
  getNowDate,
  isEqual,
  isArraysEqual,
  areArraysEqual,
  unflatenObject,
  flattenObject,
  mountSubFilter,
};
